import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Building2, User, Mail, Phone, Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { Brand } from '@/components/brand/Brand';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Company info
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    // Owner info
    firstName: '',
    lastName: '',
    ownerEmail: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleNextStep = () => {
    if (!formData.companyName || !formData.companyEmail) {
      toast.error('Please fill in company name and email');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.ownerEmail || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/register`, {
        name: formData.companyName,
        email: formData.companyEmail,
        phone: formData.companyPhone,
        owner_first_name: formData.firstName,
        owner_last_name: formData.lastName,
        owner_email: formData.ownerEmail,
        password: formData.password
      });

      if (response.data.success) {
        toast.success('Registration successful! Please log in.');
        // Store company info
        localStorage.setItem('companyId', response.data.company.id);
        localStorage.setItem('companyName', response.data.company.name);
        navigate('/login');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] to-[#F5F3F0] flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 flex items-center justify-center mb-4">
            <Brand size="lg" variant="mark" testId="register-logo" />
          </div>
          <CardTitle className="text-2xl font-bold text-[#1A1A1A]">
            Create Your Account
          </CardTitle>
          <CardDescription className="text-gray-600">
            Set up your company on KEY-MADE
          </CardDescription>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 1 ? 'bg-[#70170F] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <div className={`w-16 h-1 ${step > 1 ? 'bg-[#70170F]' : 'bg-gray-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 2 ? 'bg-[#70170F] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              2
            </div>
          </div>
          <div className="flex justify-center gap-12 mt-1 text-xs text-gray-500">
            <span>Company</span>
            <span>Account</span>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4 pt-4">
            {step === 1 ? (
              <>
                {/* Company Info */}
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#70170F]" />
                    Company Name
                  </Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Your company name"
                    className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyEmail" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#70170F]" />
                    Company Email
                  </Label>
                  <Input
                    id="companyEmail"
                    name="companyEmail"
                    type="email"
                    value={formData.companyEmail}
                    onChange={handleChange}
                    placeholder="info@yourcompany.com"
                    className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="companyPhone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#70170F]" />
                    Company Phone (Optional)
                  </Label>
                  <Input
                    id="companyPhone"
                    name="companyPhone"
                    type="tel"
                    value={formData.companyPhone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                  />
                </div>
              </>
            ) : (
              <>
                {/* Owner Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ownerEmail" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[#70170F]" />
                    Your Email
                  </Label>
                  <Input
                    id="ownerEmail"
                    name="ownerEmail"
                    type="email"
                    value={formData.ownerEmail}
                    onChange={handleChange}
                    placeholder="you@yourcompany.com"
                    className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-[#70170F]" />
                    Password
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="At least 8 characters"
                    className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    className="border-gray-300 focus:border-[#70170F] focus:ring-[#70170F]"
                    required
                  />
                </div>
              </>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            {step === 1 ? (
              <Button
                type="button"
                onClick={handleNextStep}
                className="w-full bg-[#70170F] hover:bg-[#5a1209] text-white"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div className="w-full space-y-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#70170F] hover:bg-[#5a1209] text-white"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(1)}
                  className="w-full"
                >
                  Back
                </Button>
              </div>
            )}
            
            <p className="text-sm text-gray-600 text-center">
              Already have an account?{' '}
              <Link to="/login" className="text-[#70170F] hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
